import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import {
  Anchor,
  Box,
  Button,
  Card,
  Center,
  Group,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
  Alert,
  ActionIcon,
  Tooltip,
  Modal,
  useComputedColorScheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconRefresh, IconAlertCircle, IconLock, IconShieldLock, IconDeviceMobile } from '@tabler/icons-react';
import { useTranslation } from '../i18n/LocaleContext';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { LocaleSwitcher } from '../components/LocaleSwitcher';
import { Logo } from '../components/Logo';
import { useSession } from '../auth/SessionContext';
import { authApi } from '../api/auth';
import { ApiCallError } from '../api/client';

export function LoginPage() {
  // Resolves 'auto' to whatever the OS/user actually picked, so the captcha
  // widget always matches the dashboard's current light/dark appearance.
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const { state, login } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale, t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  // hCaptcha only honours a *custom theme object* on Enterprise sitekeys; on
  // standard sitekeys such an object is ignored and the widget falls back to its
  // default (light/white) look. The built-in string themes ('light' | 'dark'),
  // however, work on every plan — so drive the widget with the string that
  // matches the app's current scheme. 'auto' is already resolved by
  // useComputedColorScheme above.
  const hcaptchaTheme = colorScheme === 'dark' ? 'dark' : 'light';
  const [error, setError] = useState<string | null>(null);
  const [captcha, setCaptcha] = useState<string>('—');
  const [captchaId, setCaptchaId] = useState<string>('');
  const [captchaLoading, setCaptchaLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  // Errors shown INSIDE the MFA modal (wrong code), separate from the main
  // form's `error` so the two steps don't clobber each other's messages.
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [hcaptchaToken, setHcaptchaToken] = useState<string | null>(null);
  const hcaptchaRef = useRef<any>(null);
  const [hcaptchaKey, setHcaptchaKey] = useState(0);

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
      captcha: '',
    },
    validate: {
      email: (v) => (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v) ? null : t('auth.errInvalidEmail')),
      password: (v) => (v.length >= 8 ? null : t('auth.errPasswordTooShort')),
    },
  });

  const refreshCaptcha = async () => {
    setCaptchaLoading(true);
    try {
      const c = await authApi.getCaptcha();
      setCaptcha(c.code);
      setCaptchaId(c.captchaId);
    } catch {
      setCaptcha('CAPTCHA_OFFLINE');
      setCaptchaId('');
    } finally {
      setCaptchaLoading(false);
    }
  };

  useEffect(() => {
    const sitekey = (import.meta.env as any).VITE_HCAPTCHA_SITEKEY;
    if (!sitekey) void refreshCaptcha();
  }, []);

  useEffect(() => {
    setHcaptchaKey((key) => key + 1);
  }, [locale]);

  if (state.status === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  // Reset the captcha widget + clear the token. MUST be called after ANY failed
  // login attempt: the backend already consumed the token in its siteverify call,
  // so a retry with the same token gets `invalid-input-response` from hCaptcha
  // (surfaced as captcha_mismatch). Remounting via a new key gives a fresh
  // challenge; onVerify then sets a new token. Legacy code-captcha path refreshes
  // its code instead.
  const resetCaptchaWidget = () => {
    const sitekey = (import.meta.env as any).VITE_HCAPTCHA_SITEKEY;
    setHcaptchaToken(null);
    if (sitekey) setHcaptchaKey((k) => k + 1);
    else void refreshCaptcha();
  };

  // Single login attempt, shared by the first-factor form submit (mfaCode='')
  // and the MFA modal's Verify button (mfaCode=<entered>). Email/password/
  // captcha are read from the form so the modal doesn't need its own copies.
  const runLogin = async (mfaCodeValue: string) => {
    setSubmitting(true);
    try {
      const values = form.values;
      const sitekey = (import.meta.env as any).VITE_HCAPTCHA_SITEKEY;
      await login({
        email: values.email,
        password: values.password,
        captcha: values.captcha || undefined,
        captchaId: captchaId || undefined,
        hcaptchaToken: sitekey ? hcaptchaToken ?? undefined : undefined,
        mfaCode: mfaCodeValue.trim() || undefined,
      });
      const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof ApiCallError) {
        // Backend attaches { attemptCount, maxAttempts, suspended, suspendedReason,
        // cascadedMerchant } in `detail` for any failure that counts toward the
        // 3-strike lockout. We surface the counter and switch to the "suspended"
        // wording the moment the threshold is hit.
        const d = (err.detail ?? {}) as {
          attemptCount?: number;
          maxAttempts?: number;
          suspended?: boolean;
          cascadedMerchant?: boolean;
          suspendedReason?: string;
        };
        const counter =
          typeof d.attemptCount === 'number' && typeof d.maxAttempts === 'number'
            ? { count: d.attemptCount, max: d.maxAttempts }
            : null;
        const suspendMsg = () =>
          t('auth.errAccountSuspendedNow', { max: d.maxAttempts ?? 3 }) +
          (d.cascadedMerchant ? ' ' + t('auth.errMerchantSuspendedNow') : '');

        if (err.code === 'mfa_required') {
          // Password + captcha are correct — this is NOT an error. Open the
          // dedicated 2FA modal so the user clearly knows to enter their
          // authenticator code.
          setError(null);
          setMfaError(null);
          setMfaRequired(true);
        } else if (err.code === 'invalid_mfa_code') {
          if (d.suspended) {
            // Too many wrong codes → account locked; drop back to the form.
            setMfaRequired(false);
            setMfaCode('');
            resetCaptchaWidget();
            setError(suspendMsg());
          } else {
            // Keep the modal open and show the error inside it for a retry.
            setMfaRequired(true);
            setMfaError(counter ? t('auth.errMfaInvalidWithCount', counter) : t('auth.errMfaInvalid'));
          }
        } else if (err.code === 'captcha_mismatch' || err.code === 'captcha_required') {
          // captcha is single-use; if it expired mid-MFA, restart factor one.
          setMfaRequired(false);
          setMfaCode('');
          setMfaError(null);
          setError(t('auth.errCaptchaMismatch'));
          resetCaptchaWidget();
        } else {
          // Any other (first-factor) error shows on the main form; close the modal.
          // The token was consumed by the backend's captcha verify, so reset the
          // widget — otherwise the next attempt reuses a dead token and fails
          // captcha instead of showing the real error again.
          setMfaRequired(false);
          setMfaCode('');
          resetCaptchaWidget();
          if (err.code === 'account_locked') setError(t('auth.errAccountLocked'));
          else if (err.code === 'account_suspended') setError(t('auth.errAccountSuspended'));
          else if (err.code === 'merchant_suspended') setError(t('auth.errMerchantSuspended'));
          else if (err.code === 'ip_whitelist_not_configured') setError(t('auth.errIpWhitelistNotConfigured'));
          else if (err.code === 'concurrent_login') setError(t('auth.errConcurrentLogin'));
          else if (err.code === 'ip_not_whitelisted') {
            if (d.suspended) setError(suspendMsg());
            else if (counter) {
              const m = err.message.match(/IP (\S+)/);
              setError(t('auth.errIpNotWhitelistedWithCount', { ...counter, ip: m?.[1] ?? '' }));
            } else setError(t('auth.errIpNotWhitelisted'));
          } else if (err.code === 'invalid_credentials') {
            if (d.suspended) setError(suspendMsg());
            else if (counter) setError(t('auth.errInvalidCredentialsWithCount', counter));
            else setError(t('auth.errInvalidCredentials'));
          } else setError(`${err.code}: ${err.message}`);
        }
      } else {
        setMfaRequired(false);
        resetCaptchaWidget();
        setError((err as Error).message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = form.onSubmit(() => {
    setError(null);
    void runLogin('');
  });
  const closeMfaModal = () => {
    setMfaRequired(false);
    setMfaCode('');
    setMfaError(null);
  };

  return (
    <Center
      mih="100vh"
      style={{
        background:
          'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-8))',
        position: 'relative',
      }}
    >
      <Box
        style={{
          position: 'absolute',
          top: 16,
          right: 16,
        }}
      >
        <LocaleSwitcher />
      </Box>
      <Box w="100%" maw={420} px="md">
        <Stack align="center" mb="lg">
          <Logo height="clamp(32px, 8vw, 44px)" />
          <Title order={2}>{t('auth.loginTitle')}</Title>
          <Text c="dimmed" size="sm" ta="center">
            {t('auth.loginTagline')}
          </Text>
        </Stack>

        <Card>
          <form onSubmit={onSubmit}>
            <Stack>
              <TextInput
                label={t('auth.email')}
                placeholder={t('auth.emailPlaceholder')}
                type="email"
                autoComplete="username"
                {...form.getInputProps('email')}
              />
              <PasswordInput
                label={t('auth.password')}
                placeholder={t('auth.passwordPlaceholder')}
                autoComplete="current-password"
                leftSection={<IconLock size={16} />}
                {...form.getInputProps('password')}
              />

              {/* Captcha belongs to the first factor only. Once we advance to
                  the MFA step we hide it — the value already entered is still
                  submitted with the MFA code, so the user solves it just once. */}
              {!mfaRequired && (
                (() => {
                  const sitekey = (import.meta.env as any).VITE_HCAPTCHA_SITEKEY;
                  if (sitekey) {
                    return (
                      <Box>
                        <HCaptcha
                          key={`${hcaptchaKey}-${locale}-${colorScheme}`}
                          ref={hcaptchaRef}
                          sitekey={sitekey}
                          theme={hcaptchaTheme}
                          languageOverride={locale}
                          onVerify={(token: string) => setHcaptchaToken(token)}
                          onExpire={() => setHcaptchaToken(null)}
                        />
                      </Box>
                    );
                  }
                  return (
                    <Group align="end" gap="xs">
                      <TextInput
                        label={t('auth.captcha')}
                        placeholder={t('auth.captchaPlaceholder')}
                        description={t('auth.captchaCurrent', {
                          code: captchaLoading ? '…' : captcha,
                        })}
                        style={{ flex: 1 }}
                        {...form.getInputProps('captcha')}
                      />
                      <Tooltip label={t('auth.refreshCaptcha')}>
                        <ActionIcon
                          variant="default"
                          size={36}
                          onClick={() => void refreshCaptcha()}
                          loading={captchaLoading}
                        >
                          <IconRefresh size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  );
                })()
              )}

              {error && (
                <Alert color="red" icon={<IconAlertCircle size={16} />} variant="light">
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                loading={submitting}
                size="md"
                disabled={
                  !!( (import.meta.env as any).VITE_HCAPTCHA_SITEKEY && !hcaptchaToken && !mfaRequired )
                }
              >
                {t('auth.signIn')}
              </Button>
            </Stack>
          </form>
        </Card>

        <Text c="dimmed" size="xs" ta="center" mt="md">
          {t('auth.needAccess')}{' '}
          <Anchor href="mailto:admin@transpay.local">{t('auth.contactAdmin')}</Anchor>
        </Text>
      </Box>

      {/* Dedicated 2FA step. Opens only after email+password+captcha pass and
          the server asks for an MFA code — a distinct popup so the user knows
          this is the expected second factor, not an error. */}
      <Modal
        opened={mfaRequired}
        onClose={() => { if (!submitting) closeMfaModal(); }}
        centered
        closeOnClickOutside={!submitting}
        title={
          <Group gap="sm">
            <IconShieldLock size={18} />
            <Text fw={600}>{t('auth.mfaModalTitle')}</Text>
          </Group>
        }
      >
        <Stack>
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <IconDeviceMobile size={28} style={{ flexShrink: 0, opacity: 0.7 }} />
            <Text size="sm" c="dimmed">
              {t('auth.mfaModalIntro')}
            </Text>
          </Group>
          <TextInput
            label={t('auth.mfaCodeLabel')}
            placeholder="123456"
            inputMode="numeric"
            autoComplete="one-time-code"
            leftSection={<IconLock size={16} />}
            value={mfaCode}
            data-autofocus
            onChange={(e) => setMfaCode(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && mfaCode.trim().length > 0) {
                setMfaError(null);
                void runLogin(mfaCode);
              }
            }}
          />
          {mfaError && (
            <Alert color="red" icon={<IconAlertCircle size={16} />} variant="light">
              {mfaError}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={closeMfaModal} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button
              loading={submitting}
              disabled={mfaCode.trim().length === 0}
              onClick={() => {
                setMfaError(null);
                void runLogin(mfaCode);
              }}
            >
              {t('auth.mfaModalVerify')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Center>
  );
}
